<script setup lang="ts">
import { computed, ref } from 'vue'
import { Field, FieldContent, FieldLabel, FieldError, FieldGroup } from '@/components/shadcn'
import { JInput, JTextarea, JCheckbox, JSwitch, JCombo, JRadio, JSearchCombo, JSearchInput, JDatepicker, JEditor, JIcon, JPopover } from '@/components/atoms'
import JDateRangeField from '@/components/molecules/JDateRangeField.vue'
import JNumberRangeField from '@/components/molecules/JNumberRangeField.vue'
import { cn } from '@/lib/utils'
import { useBreakpoint } from '@/composables/useBreakpoint'

// 컴포넌트 타입 정의
type ComponentType = 'input' | 'textarea' | 'checkbox' | 'switch' | 'combo' | 'radio' | 'searchCombo' | 'searchInput' | 'datepicker' | 'editor' | 'dateRange' | 'numberRange'

// FormField 자체의 props (레이아웃 관련)
const FORM_FIELD_PROPS = [
  'class',
  'label',
  'description',
  'errorMsg',
  'type',
  'inlineLabel',
  'orientation',
  'labelAlign',
  'labelWidth',
  'radioDirection',
  'editorHeight',
  'fillHeight',
  'rangeOptions',
] as const

const props = withDefaults(
  defineProps<{
    // ============ FormField 자체 props (레이아웃만) ============
    /** 추가 클래스 (외부 커스터마이징용) */
    class?: string
    /** 필드 레이블 */
    label?: string
    /** 필드 설명 (레이블 아래 표시) */
    description?: string
    /** 에러 메시지 */
    errorMsg?: string
    /** 컴포넌트 타입 (렌더링할 컴포넌트 지정) */
    type?: ComponentType
    /** 체크박스/스위치 타입일 때만 사용하는 옆 라벨 */
    inlineLabel?: string
    /** 레이블과 컨트롤의 배치 방향 */
    orientation?: 'vertical' | 'horizontal' | 'responsive'
    /** 레이블 텍스트 정렬 */
    labelAlign?: 'left' | 'middle' | 'right'
    /** 레이블 영역 너비 (horizontal orientation일 때만 적용) */
    labelWidth?: string

    // ============ 내부 컴포넌트로 전달할 공통 props ============
    /** Input 요소의 id (label for와 연결) */
    id?: string
    /** v-model로 양방향 데이터 바인딩 */
    modelValue?: any
    /** 입력 전 표시되는 안내문 (Input/Textarea/Select/Combobox) */
    placeholder?: string
    /** 비활성화 상태 (전체) */
    disabled?: boolean
    /** 읽기 전용 상태 (Input/Textarea) */
    readonly?: boolean
    /** 필수 입력/선택 여부 (전체) */
    required?: boolean
    /** form 데이터 전송 시 키 이름 (전체) */
    name?: string
    /** 스타일 테마 지정 (J-prefixed 컴포넌트의 styleType) */
    styleType?: string

    // ============ Input 전용 props ============
    /** Input 타입 (text, email, password 등) */
    inputType?: string


    // ============ Select/Combobox/Radio 전용 props ============
    /** 선택 가능한 항목 배열 */
    options?: Array<{ label: string; value: string | number; disabled?: boolean }>
    
    // ============ Select/Combobox 전용 props ============
    /** 다중 선택 허용 여부 */
    multiple?: boolean


    // ============ Radio 전용 props ============
    /** Radio 옵션 나열 방향 */
    radioDirection?: 'horizontal' | 'vertical'

    // ============ Switch 전용 props ============
    /** 상태 라벨 노출 여부 (기본: true) */
    showStateLabel?: boolean
    /** 활성(Y) 상태 라벨 텍스트 (기본: '활성') */
    activeLabel?: string
    /** 비활성(N) 상태 라벨 텍스트 (기본: '비활성') */
    inactiveLabel?: string

    // ============ Editor 전용 props ============
    /** 에디터 높이 (기본값: '300px') */
    editorHeight?: string | number
    /** 에디터가 남은 세로 공간을 모두 채우도록 확장 (type='editor'일 때만 적용) */
    fillHeight?: boolean

    // ============ SearchInput 전용 props ============
    /** 찾기 버튼 텍스트 (기본: '찾기') */
    buttonText?: string
    /** 찾기 버튼만 별도 비활성화 */
    buttonDisabled?: boolean
    /** 찾기 버튼에 돋보기 아이콘 표시 (기본: true) */
    showIcon?: boolean

    // ============ Range 타입 전용 props (dateRange / numberRange) ============
    /** 범위 입력 옵션 */
    rangeOptions?: {
      min?: string | number
      max?: string | number
      step?: number
      suffix?: string
      separator?: string
      placeholder?: { start?: string; end?: string }
    }
  }>(),
  {
    type: 'input',
    orientation: 'horizontal',
    labelAlign: 'left',
    labelWidth: '80px',
    radioDirection: 'horizontal',
  }
)

// 이벤트 정의
const emit = defineEmits<{
  'update:modelValue': [value: any]
  'change': [value: any]
  'focus': [event: FocusEvent]
  'blur': [event: FocusEvent]
  'save': [value: string]
  'search': []
}>()

// description 팝오버 상태
const descOpen = ref(false)
let descTimer: ReturnType<typeof setTimeout> | null = null
const showDesc = () => {
  if (descTimer) clearTimeout(descTimer)
  descOpen.value = true
}
const hideDesc = () => {
  if (descTimer) clearTimeout(descTimer)
  descTimer = setTimeout(() => { descOpen.value = false }, 100)
}

// 내부 에러 상태 (props.error가 없을 때만 사용)
const internalError = ref<string>('')

// 최종 에러 메시지 (외부 errorMsg가 우선)
const finalError = computed(() => props.errorMsg || internalError.value)

// FormField 자체 props와 내부 컴포넌트 props 분리
const controlProps = computed(() => {
  const result: Record<string, any> = {}
  const propsObj = props as Record<string, any>
  
  for (const key in propsObj) {
    // FormField 자체 props는 제외
    if (!FORM_FIELD_PROPS.includes(key as any)) {
      result[key] = propsObj[key]
    }
  }
  
  // inputType을 type으로 변환 (JInput 컴포넌트에서 사용)
  if (props.inputType && props.type === 'input') {
    result.type = props.inputType
    delete result.inputType // inputType은 제거
    
    // placeholder가 없으면 inputType에 따라 기본값 설정
    if (!props.placeholder) {
      const defaultPlaceholders: Record<string, string> = {
        'text': '텍스트를 입력하세요',
        'email': '이메일을 입력하세요',
        'password': '비밀번호를 입력하세요',
        'tel': '전화번호를 입력하세요',
        'url': 'URL을 입력하세요',
        'number': '숫자를 입력하세요',
        'search': '검색어를 입력하세요',
        'date': '날짜를 선택하세요',
        'time': '시간을 선택하세요',
        'datetime-local': '날짜와 시간을 선택하세요',
        'month': '월을 선택하세요',
        'week': '주를 선택하세요',
      }
      
      result.placeholder = defaultPlaceholders[props.inputType] || '입력하세요'
    }
  }
  
  // radioDirection을 styletype으로 변환 (JRadio 컴포넌트에서 사용)
  if (props.radioDirection && props.type === 'radio') {
    result.styletype = props.radioDirection
    delete result.radioDirection // radioDirection은 제거
  }

  // editorHeight를 height로 변환 (JEditor 컴포넌트에서 사용)
  if (props.type === 'editor') {
    result.height = props.fillHeight ? '100%' : (props.editorHeight ?? '300px')
    delete result.editorHeight
    delete result.fillHeight
  }

  return result
})

  // Built-in validation
  const validateField = (currentValue?: any) => {
    internalError.value = ''

    // 현재 값 또는 props.modelValue 사용
    const value = currentValue !== undefined ? currentValue : props.modelValue

    // Required 체크
    if (props.required) {
      if (props.type === 'checkbox' || props.type === 'switch') {
        if (value !== 'Y') {
          internalError.value = '필수 항목입니다.'
          return
        }
      } else if (props.type === 'dateRange') {
        const v = value as { start: string | null; end: string | null }
        if (!v?.start || !v?.end) {
          internalError.value = '시작일과 종료일을 모두 입력해 주세요.'
          return
        }
      } else if (props.type === 'numberRange') {
        const v = value as { start: number | null; end: number | null }
        if (v?.start == null || v?.end == null) {
          internalError.value = '최소값과 최대값을 모두 입력해 주세요.'
          return
        }
      } else {
        if (value === null || value === undefined || value === '') {
          internalError.value = '필수 입력 항목입니다.'
          return
        }
      }
    }

    // Range 순서 검증 (required 여부 무관)
    if (props.type === 'dateRange') {
      const v = value as { start: string | null; end: string | null }
      if (v?.start && v?.end && v.start > v.end) {
        internalError.value = '시작일은 종료일 이전이어야 합니다.'
      }
    } else if (props.type === 'numberRange') {
      const v = value as { start: number | null; end: number | null }
      if (v?.start != null && v?.end != null && v.start > v.end) {
        internalError.value = '최소값은 최대값보다 클 수 없습니다.'
      }
    }
  }

  // 초기 로드 시 validation 실행 (blur 이벤트에서만)
  const shouldValidateOnMount = computed(() => {
    // Storybook에서 초기값이 있는 경우 validation 스킵
    if (props.modelValue !== null && props.modelValue !== undefined && props.modelValue !== '') {
      return false
    }
    return true
  })

// 이벤트 핸들러
const handleUpdateModelValue = (value: any) => {
  emit('update:modelValue', value)
  
  // 값이 변경되면 즉시 validation 실행 (현재 값 전달)
  validateField(value)
}

const handleChange = (value: any) => {
  emit('change', value)
  
  // change 이벤트에서도 validation 실행 (현재 값 전달)
  validateField(value)
}

const handleSave = (value: string) => {
  emit('save', value)
}

const handleSearch = () => {
  emit('search')
}

const handleFocus = (event: FocusEvent) => {
  emit('focus', event)
}

  const handleBlur = (event: FocusEvent) => {
    // blur 시에만 validation 실행 (초기 로드 시에는 실행하지 않음)
    if (shouldValidateOnMount.value) {
      validateField()
    }
    emit('blur', event)
  }

// 레이블 정렬 클래스 (레이블 영역 내에서 레이블의 위치 제어)
const labelAlignClass = computed(() => {
  // horizontal일 때는 레이블 영역 내에서 레이블의 위치를 제어
  const mapHorizontal = { 
    left: 'justify-start',    // 레이블 영역의 왼쪽에 레이블 위치
    middle: 'justify-center', // 레이블 영역의 가운데에 레이블 위치
    right: 'justify-end'      // 레이블 영역의 오른쪽에 레이블 위치 (input과 가까이)
  }
  // vertical일 때는 텍스트 정렬만 제어
  const mapVertical = { left: 'text-left', middle: 'text-center', right: 'text-right' }
  return effectiveOrientation.value === 'horizontal' ? mapHorizontal[props.labelAlign] : mapVertical[props.labelAlign]
})

/** 행높이 토큰 클래스 매핑 */
const densityClass = computed(() => {
  switch (props.styleType) {
    case 'md': return 'form-density-md'
    case 'lg': return 'form-density-lg'
    default:   return 'form-density-sm'  // 기본값 변경: md → sm (컴팩트 디자인)
  }
})

/** 컨트롤 클래스 (datepicker는 최대 너비 제한, radio vertical은 높이 제한 없음) */
const controlClass = computed(() => {
  const baseClass = 'h-[var(--ctl-h)] leading-[var(--ctl-h)]'

  if (props.type === 'datepicker') {
    return `${baseClass} max-w-xs`
  }

  // Editor: fillHeight면 h-full, 아니면 높이 제한 없음
  if (props.type === 'editor') {
    return props.fillHeight ? 'h-full w-full' : ''
  }

  // Radio vertical: 고정 높이 제한 없음
  if (props.type === 'radio' && props.radioDirection === 'vertical') {
    return ''
  }

  return baseClass
})

type MappedComponentType = Exclude<ComponentType, 'dateRange' | 'numberRange'>

// 컴포넌트 매핑 (dateRange/numberRange는 별도 템플릿 분기에서 직접 렌더)
const componentMap: Record<MappedComponentType, any> = {
  input: JInput,
  textarea: JTextarea,
  checkbox: JCheckbox,
  switch: JSwitch,
  combo: JCombo,
  radio: JRadio,
  searchCombo: JSearchCombo,
  searchInput: JSearchInput,
  datepicker: JDatepicker,
  editor: JEditor,
}

// type에 따라 렌더링할 컴포넌트 결정 (range 타입은 별도 분기)
const resolvedComponent = computed(() => {
  return componentMap[props.type as MappedComponentType] || JInput
})

/** fillHeight + editor 조합 여부 */
const fillHeightEditor = computed(() => props.fillHeight && props.type === 'editor')

/** responsive orientation: 모바일에서 vertical, 데스크톱에서 horizontal */
const { isDesktop } = useBreakpoint()
const effectiveOrientation = computed(() => {
  if (props.orientation === 'responsive') {
    return isDesktop.value ? 'horizontal' : 'vertical'
  }
  return props.orientation
})

// 외부에서 수동으로 에러 클리어 가능하도록 expose
defineExpose({
  clearError: () => { internalError.value = '' }
})
</script>

<template>
  <div :class="cn(fillHeightEditor ? 'flex-1 flex flex-col min-h-0' : type === 'editor' ? 'flex-1 min-w-0' : 'space-y-2 flex-1 min-w-0', densityClass, props.class)">
    <FieldGroup :class="fillHeightEditor ? 'flex-1 flex flex-col min-h-0' : ''">
      <Field
:class="[
          effectiveOrientation === 'horizontal'
            ? 'grid grid-cols-[var(--label-w,8rem)_1fr] items-start space-y-0 gap-2'
            : type === 'editor' ? 'space-y-0 gap-0' : 'space-y-1 gap-1',
          fillHeightEditor ? 'flex-1 min-h-0' : ''
        ]"
        :style="effectiveOrientation === 'horizontal' && labelWidth ? `--label-w:${labelWidth};` : ''"
      >
        <!-- 메인 라벨 (필수표기 포함) -->
        <FieldLabel 
          :for="id" 
          :class="[
            'text-xs font-medium whitespace-nowrap',  // 컴팩트 디자인 + 라벨-값 weight 차별화
            effectiveOrientation === 'horizontal'
              ? (type === 'editor' ? 'flex items-start pt-1 w-full' : 'flex items-center h-[var(--ctl-h)] w-full')
              : 'flex items-center',
            labelAlignClass
          ]"
        >
          {{ label }}
          <span v-if="required" class="text-destructive ml-0.5">*</span>
          <template v-if="description">
            <JPopover :open="descOpen" position="right" align="start" :side-offset="6" styletype="auto" @update:open="descOpen = $event">
              <template #trigger>
                <button
                  type="button"
                  class="ml-1 inline-flex items-center shrink-0 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded"
                  @mouseenter="showDesc"
                  @mouseleave="hideDesc"
                  @focus="showDesc"
                  @blur="hideDesc"
                  @click.stop.prevent="descOpen = !descOpen"
                >
                  <JIcon name="info" size="xs" />
                  <span class="sr-only">설명 보기</span>
                </button>
              </template>
              <div
                class="text-[11px] text-foreground leading-relaxed border-l-2 border-primary pl-2"
                @mouseenter="showDesc"
                @mouseleave="hideDesc"
              >
                <slot name="description">{{ description }}</slot>
              </div>
            </JPopover>
            <span class="sr-only">{{ description }}</span>
          </template>
        </FieldLabel>

        <FieldContent
          :class="[
            effectiveOrientation === 'horizontal'
            ? 'min-h-[var(--ctl-h)] flex flex-col justify-start gap-0.5 mt-0'
            : 'space-y-2 gap-0',
            fillHeightEditor ? 'flex-1 flex flex-col min-h-0' : ''
          ]"
        >
          <!-- 체크박스/스위치: 항상 가로 정렬로 컨트롤 + 인라인 라벨 묶기 -->
          <FieldGroup v-if="type === 'checkbox' || type === 'switch'" data-slot="checkbox-group">
            <!-- 부모 orientation과 무관하게 수평 정렬 고정 -->
            <Field orientation="horizontal" class="flex gap-2 space-y-0 h-[var(--ctl-h)] leading-[var(--ctl-h)] items-center">
              <component
                :is="resolvedComponent"
                v-bind="controlProps"
                @update:model-value="handleUpdateModelValue"
                @change="handleChange"
                @focus="handleFocus"
                @blur="handleBlur"
              />
              <FieldLabel
                v-if="inlineLabel && type === 'checkbox'"
                :for="id"
                class="text-xs font-normal m-0 h-[var(--ctl-h)] leading-[var(--ctl-h)]"
              >
                {{ inlineLabel }}
              </FieldLabel>
            </Field>
          </FieldGroup>

          <!-- Radio 버튼: radioDirection에 따라 분기 처리 -->
          <FieldGroup v-else-if="type === 'radio'">
            <component
              :is="resolvedComponent"
              v-bind="controlProps"
              :class="controlClass"
              @update:model-value="handleUpdateModelValue"
              @change="handleChange"
              @focus="handleFocus"
              @blur="handleBlur"
            />
          </FieldGroup>

          <!-- Range 타입: JDateRangeField / JNumberRangeField -->
          <FieldGroup v-else-if="type === 'dateRange' || type === 'numberRange'">
            <JDateRangeField
              v-if="type === 'dateRange'"
              :id="id"
              :model-value="modelValue"
              :disabled="disabled"
              :required="required"
              :name="name"
              v-bind="(rangeOptions as any)"
              @update:model-value="handleUpdateModelValue"
              @change="handleChange"
              @focus="handleFocus"
              @blur="handleBlur"
            />
            <JNumberRangeField
              v-else
              :id="id"
              :model-value="modelValue"
              :disabled="disabled"
              :required="required"
              :name="name"
              v-bind="(rangeOptions as any)"
              @update:model-value="handleUpdateModelValue"
              @change="handleChange"
              @focus="handleFocus"
              @blur="handleBlur"
            />
          </FieldGroup>

          <!-- 그 외 컨트롤: orientation 규칙 그대로 따름 -->
          <FieldGroup v-else :class="fillHeightEditor ? 'flex-1 flex flex-col min-h-0' : ''">
            <component
              :is="resolvedComponent"
              v-bind="controlProps"
              :class="controlClass"
              @update:model-value="handleUpdateModelValue"
              @change="handleChange"
              @focus="handleFocus"
              @blur="handleBlur"
              @save="handleSave"
              @search="handleSearch"
            />
          </FieldGroup>
          
          <!-- 에러: 컨트롤 바로 아래 표시 -->
          <FieldContent v-if="finalError">
            <FieldError>
              {{ finalError }}
            </FieldError>
          </FieldContent>
        </FieldContent>
      </Field>
    </FieldGroup>
  </div>
</template>



<style scoped>
/* 높이 토큰(밀도) — :root의 --j-ctl-h-* 를 참조, 앱에서 override 가능 */
.form-density-sm { --ctl-h: var(--j-ctl-h-sm, 1.75rem); }
.form-density-md { --ctl-h: var(--j-ctl-h-md, 2.25rem); }
.form-density-lg { --ctl-h: var(--j-ctl-h-lg, 2.5rem);  }
</style>
